import { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getApiUrl } from "../../api";
import { ChainResidues, JobControllerApi } from "../../api/openapi-generator";
import { JobInfoContext } from "../../job-info-context";
import { Ligand, MolstarViewerRefType, Protein, VisualizedResidue } from "../../molstar/types";
import { useViewerContext } from "./viewer-context";
import MolstarViewer from "../../molstar/molstar-viewer";
import { FocusInhibitorDetail, FocusResidueDetail } from "./viewer-event";

const PocketColors = [
  "#d95f02", "#e7298a", "#66a61e", "#a6761d", "#666666"
];

type UrlWtMut = {wt: string, mut: string};

export function MolstarViewerWithContext() {
  const { id: jobId } = useContext(JobInfoContext);

  const {
    annotations,
    wtEnabled, mutEnabled,
    wtMutRes, mutMutRes,
    wtEssRes, mutEssRes,
    wtPockets, mutPockets,
  } = useViewerContext();

  const mutatedPdbPositions = annotations?.mutatedPdbPositions;
  const essentialResidues = annotations?.essentialResidues;
  const pocketMap = annotations?.pocketMap;

  const [proteinUrl, setProteinUrl] = useState<{wt: string, mut: string} | null>(null);
  useEffect(() => {
    Promise.all([
      getApiUrl(JobControllerApi, 'wildTypeCytoplasmicStructureRaw', {id: jobId}),
      getApiUrl(JobControllerApi, 'mutantStructureRaw', {id: jobId}),
    ])
      .then(([wt, mut]) => setProteinUrl({wt, mut}));
    return () => {
      setProteinUrl(null);
    }
  }, [jobId]);

  const ligandUrlFormat = useLigandUrlFormat(jobId);

  const wtProtein = useProteinProp(proteinUrl?.wt, 'wt', wtEnabled, '#1b9e77');
  const mutProtein = useProteinProp(proteinUrl?.mut, 'mut', mutEnabled, '#7570b3');
  const { ligands, wtLigands, mutLigands } = useLigandsProp(ligandUrlFormat);

  const pocketResidues = useVisualizedPocketResidues(pocketMap);

  const wtResiduesMut = useVisualizedResiduesProp(mutatedPdbPositions, wtMutRes, '#77CB2B');
  const wtResiduesEss = useVisualizedResiduesProp(essentialResidues, wtEssRes, '#e41a1c');
  const wtResidues = useConcat(
    wtPockets ? pocketResidues : null,
    useConcat(wtResiduesMut, wtResiduesEss),
  );

  const mutResiduesMut = useVisualizedResiduesProp(mutatedPdbPositions, mutMutRes, '#AA4EB4');
  const mutResiduesEss = useVisualizedResiduesProp(essentialResidues, mutEssRes, '#D80014');
  const mutResidues = useConcat(
    mutPockets ? pocketResidues : null,
    useConcat(mutResiduesMut, mutResiduesEss),
  );

  const ref = useRef<MolstarViewerRefType>(null);

  const focusResidueHandler = useFocusResidueHandler(ref, wtEnabled, mutEnabled);
  useEffect(() => {
    document.addEventListener('focusResidue', focusResidueHandler);
    return () => {
      document.removeEventListener('focusResidue', focusResidueHandler);
    }
  }, [focusResidueHandler]);

  const focusWtInhHandler = useFocusInhibitorHandler(ref, wtLigands);
  const focusMutInhHandler = useFocusInhibitorHandler(ref, mutLigands);
  useEffect(() => {
    document.addEventListener('focusWtInhibitor', focusWtInhHandler);
    document.addEventListener('focusMutInhibitor', focusMutInhHandler);
    return () => {
      document.removeEventListener('focusWtInhibitor', focusWtInhHandler);
      document.removeEventListener('focusMutInhibitor', focusMutInhHandler);
    }
  }, [focusWtInhHandler, focusMutInhHandler]);

  return (
    <MolstarViewer
      forwardedRef={ref}
      wtProtein={wtProtein}
      mutProtein={mutProtein}
      wtResidues={wtResidues}
      mutResidues={mutResidues}
      ligands={ligands}
    />
  )
}

function useProteinProp(url: string | undefined, label: string, enabled: boolean, color: string): Protein | undefined {

  const { representation } = useViewerContext();

  return useMemo(() => {
    if (!url || !enabled) {
      return undefined;
    }
    return {
      url,
      format: 'pdb',
      label,
      color,
      representationType: representation,
    }
  }, [url, enabled, color, representation]);

}

function useVisualizedResiduesProp(chainResidues: ChainResidues[] | undefined, enabled: boolean, color: string): VisualizedResidue[] {
  return useMemo(() => {
    if (!chainResidues || !enabled) return [];
    return chainResidues.flatMap(
      ({ chain, residues }) => {
        return residues.map(r => ({
          chain,
          position: r.position,
          color,
          sideChain: true,
        }));
      }
    );
  }, [chainResidues, enabled, color]);
}

function useVisualizedPocketResidues(pocketMap: Record<string, ChainResidues[]> | undefined): VisualizedResidue[] {
  // pocketMap has format: pocket rank -> { chain, residues }
  return useMemo(() => {
    if (!pocketMap) return [];
    return Object.entries(pocketMap).flatMap(
      ([rank, chainPositions]) => {
        const color = PocketColors[(Number(rank) - 1) % PocketColors.length];
        return chainPositions.flatMap(
          ({ chain, residues }) => {
            return residues.map(r => ({
              chain,
              position: r.position,
              color,
              sideChain: false,
            }))
          }
        )
      }
    );
  }, [pocketMap]);
}

function useConcat<T>(a: T[] | null, b: T[] | null) {
  return useMemo(() => {
    return [...(a ?? []), ...(b ?? [])];
  }, [a, b]);
}

function useLigandUrlFormat(jobId: string) {
  const [urlFormat, setUrlFormat] = useState<UrlWtMut | null>(null);
  useEffect(() => {
    Promise.all([
      getApiUrl(JobControllerApi, 'wildTypeLigandPosesRaw', {id: jobId, zincId: '{}'}).then(decodeURI),
      getApiUrl(JobControllerApi, 'mutantLigandPosesRaw', {id: jobId, zincId: '{}'}).then(decodeURI),
    ])
      .then(([wt, mut]) => setUrlFormat({wt, mut}));
    return () => setUrlFormat(null);
  }, [jobId]);
  return urlFormat;
}

function useLigandsProp(ligandUrlFormat: UrlWtMut | null) {

  const {
    wtEnabled,
    mutEnabled,
    selectedInhibitors,
    wtInhibitors,
    mutInhibitors,
  } = useViewerContext();

  const wtLigands = useMemo<Record<string, Ligand>>(() => {
    if (!ligandUrlFormat) return {};
    return Object.fromEntries(selectedInhibitors.map(inh => (
      [inh.id, {
        url: ligandUrlFormat.wt.replace('{}', inh.zincId),
        label: 'wt ' + inh.zincIdShort,
        format: 'pdbqt',
        visible: wtEnabled && (wtInhibitors[inh.id] ?? false),
      }]
    )))
  }, [ligandUrlFormat, wtEnabled, selectedInhibitors, wtInhibitors]);

  const mutLigands = useMemo<Record<string, Ligand>>(() => {
    if (!ligandUrlFormat) return {};
    return Object.fromEntries(selectedInhibitors.map(inh => (
      [inh.id, {
        url: ligandUrlFormat.mut.replace('{}', inh.zincId),
        label: 'mut ' + inh.zincIdShort,
        format: 'pdbqt',
        visible: mutEnabled && (mutInhibitors[inh.id] ?? false),
      }]
    )))
  }, [ligandUrlFormat, mutEnabled, selectedInhibitors, mutInhibitors]);

  const ligands = useMemo(() => {
    return [
      ...Object.values(wtLigands),
      ...Object.values(mutLigands),
    ];
  }, [wtLigands, mutLigands]);

  return {
    ligands,
    wtLigands,
    mutLigands,
  };
}

function useFocusInhibitorHandler(
  forwardedRef: RefObject<MolstarViewerRefType>,
  ligandMap: Record<string, Ligand>,
) {
  // We use ref so that the handler reference doesn't change.
  const ligandMapRef = useRef<Record<string, Ligand>>(ligandMap);
  useEffect(() => {
    ligandMapRef.current = ligandMap;
  }, [ligandMap]);

  return useCallback((e: CustomEvent<FocusInhibitorDetail>) => {
    const {inhibitor: inh} = e.detail;
    const ligand = ligandMapRef.current[inh.id];
    if (ligand === undefined) return;
    forwardedRef.current?.focusLigand(ligand);
  }, [forwardedRef]);
}

function useFocusResidueHandler(
  forwardedRef: RefObject<MolstarViewerRefType>,
  wtEnabled: boolean,
  mutEnabled: boolean,
) {
  // We use ref so that the handler reference doesn't change.
  const enabledRef = useRef({wt: wtEnabled, mut: mutEnabled});
  useEffect(() => {
    enabledRef.current = {wt: wtEnabled, mut: mutEnabled};
  }, [wtEnabled, mutEnabled]);

  return useCallback((e: CustomEvent<FocusResidueDetail>) => {
    const {chain, position} = e.detail;
    if (enabledRef.current.wt) {
      forwardedRef.current?.focusWtResidue(chain, position);
    } else if (enabledRef.current.mut) {
      forwardedRef.current?.focusMutResidue(chain, position);
    }
  }, [forwardedRef]);
}